import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "../../lib/utils.js";

/* Clean listing table: quiet lowercase headers, hairline rows.
 *
 * The dense geometry DESIGN.md documents (a p-1 tile around 8px cells) lives
 * here rather than in per-cell overrides at every call site — five tables that
 * each pick their own padding is how they drifted apart in the first place. */
export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  );
}
export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("[&_tr]:border-b [&_tr]:border-border", className)} {...props} />;
}
export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}
export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn("border-b border-border transition-colors hover:bg-muted/70", className)}
      {...props}
    />
  );
}
export function TableHead({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "h-10 px-2 text-left align-middle text-xs font-semibold text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}
export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("p-2 align-top text-xs", className)} {...props} />;
}

/**
 * "Nothing here" inside a table, so an empty table keeps its header, its border
 * and its height instead of collapsing into a stray paragraph. Same vertical
 * room as EmptyNote outside a table.
 */
export function TableEmpty({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan} className="px-3 py-10 text-center text-sm text-muted-foreground">
          {children}
        </td>
      </tr>
    </tbody>
  );
}

/** Skeleton rows that occupy exactly the space the real rows will. */
export function TableSkeleton({ colSpan, rows = 4 }: { colSpan: number; rows?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, index) => (
        <tr key={index} className="border-b border-border last:border-0">
          <td className="p-2" colSpan={colSpan}>
            <div className="h-5 animate-pulse rounded-full bg-muted" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}
